<script>
import { mapState } from 'vuex';
import PageHeader from '@/V2/components/PageHeader.vue';

import {
  iBadge,
  iBadgeColours,
  iButton,
  iButtonStyles,
  iButtonTypes,
  iMenu,
  iMenuItem,
  iMenuTriggerAlignOptions,
  iModal,
  iPagination,
  iTable,
  iTableRow,
  iTableSortButton,
  openModal,
  showToast,
} from '@inscribe/signal';
import EmptyPage from '@/shared/components/EmptyPage.vue';
import {
  deprecatedWebhookTypes,
  documentReviewAgentWebhookTypes,
  businessComplianceAgentWebhookTypes,
  processingJobWebhookTypes,
  webhookTypes,
} from '@/V2/settings/webhooks/WebhookTypes';
import { formatDateTime } from '@/utils/date';
import palette from '@inscribe/signal/dist/palette';

export default {
  components: {
    'empty-page': EmptyPage,
    iBadge,
    iButton,
    PageHeader,
    iMenu,
    iMenuItem,
    iModal,
    iPagination,
    iTable,
    iTableRow,
    iTableSortButton,
  },
  data() {
    return {
      iBadgeColours,
      iButtonStyles,
      iButtonTypes,
      iMenuTriggerAlignOptions,
      loadingWebhooks: false,
      deleteSelectedWebhook: null,
      perPage: 8,
      currentPage: 1,
      palette,
      webhookTypes: [
        ...webhookTypes,
        ...documentReviewAgentWebhookTypes,
        ...businessComplianceAgentWebhookTypes,
        ...processingJobWebhookTypes,
        ...deprecatedWebhookTypes,
      ],
      sortType: 'url',
      sortOrder: 'asc',
    };
  },
  computed: {
    ...mapState(['webhooks']),
    displayedWebhooks() {
      const returnWebhooks = this.webhooks.filter((w) => this.sortType);
      // sort
      const sortValue = 1 * (this.sortOrder === 'asc' ? 1 : -1);
      switch (this.sortType) {
        case 'url':
          returnWebhooks.sort((wb1, wb2) =>
            wb1.url > wb2.url ? 1 * sortValue : -1 * sortValue,
          );
          break;

        case 'event':
          returnWebhooks.sort((wb1, wb2) =>
            this.webhookTypeDisplayName(wb1) > this.webhookTypeDisplayName(wb2)
              ? 1 * sortValue
              : -1 * sortValue,
          );
          break;

        case 'created':
          returnWebhooks.sort((wb1, wb2) =>
            new Date(wb1.created_at) > new Date(wb2.created_at)
              ? 1 * sortValue
              : -1 * sortValue,
          );
          break;

        case 'status':
          returnWebhooks.sort((wb1, wb2) =>
            wb1.enabled > wb2.enabled ? 1 * sortValue : -1 * sortValue,
          );
          break;
      }

      const paginationStart = this.perPage * (this.currentPage - 1);
      return returnWebhooks.slice(
        paginationStart,
        paginationStart + this.perPage,
      );
    },
    webhooksLength() {
      return this.webhooks.length;
    },
    hasWebhooks() {
      return this.webhooksLength > 0;
    },
    showPagination() {
      return this.webhooksLength > this.perPage;
    },
  },
  mounted() {
    this.loadingWebhooks = true;
    this.$store.dispatch('getWebhooks').finally(() => {
      this.loadingWebhooks = false;
    });
  },
  methods: {
    onOrderChange(field, order) {
      this.sortType = field;
      this.sortOrder = order;
    },
    formatDateTime,
    webhookTypeDisplayName(webhook) {
      return this.webhookTypes.find((type) => {
        return type.value === webhook.type;
      }).text;
    },
    startDeleteWebhook(webhook) {
      this.deleteSelectedWebhook = webhook;
      openModal('delete-webhook');
    },
    redirectToCreateWebhook() {
      this.$router.push({ name: 'webhooks-settings-create' });
    },
    redirectToEditWebhook(webhook) {
      this.$router.push({
        name: 'webhooks-settings-edit',
        params: { webhookId: webhook.id },
      });
    },
    deleteWebhook() {
      this.$store
        .dispatch('deleteWebhook', this.deleteSelectedWebhook)
        .then(() => {
          this.$store.commit('deleteGeneric', {
            objectsName: 'webhooks',
            idToDelete: this.deleteSelectedWebhook.id,
          });
          showToast({ text: 'Webhook deleted' });
        })
        .catch(() => {
          showToast({ text: 'Deleting Webhook Failed', isError: true });
        })
        .finally(() => {
          this.deleteSelectedWebhook = null;
        });
    },
  },
};
</script>

<template>
  <div class="settings-page d-flex flex-column">
    <PageHeader title="Webhooks">
      <template #primary-actions>
        <i-button
          v-if="hasWebhooks"
          data-testid="open-create-webhook"
          text="Create Webhook"
          size="md"
          @button-click="redirectToCreateWebhook()"
        />
      </template>
    </PageHeader>
    <div
      class="settings-page__body d-flex flex-column align-items-center i-px-12 i-py-11"
    >
      <div class="d-flex justify-content-center w-100">
        <div class="settings-page-view">
          <div v-if="hasWebhooks">
            <i-table data-testid="webhooks-table">
              <!-- table header -->
              <template #header>
                <i-table-row type="header">
                  <template #cells>
                    <!-- url -->
                    <div class="no-padding-x">
                      <i-table-sort-button
                        label="URL"
                        class="no-padding-x"
                        @order-change="(order) => onOrderChange('url', order)"
                      ></i-table-sort-button>
                    </div>

                    <!-- event -->
                    <div>
                      <i-table-sort-button
                        label="Event"
                        class="no-padding-x"
                        @order-change="(order) => onOrderChange('event', order)"
                      ></i-table-sort-button>
                    </div>

                    <!-- date created -->
                    <div>
                      <i-table-sort-button
                        label="Date Created"
                        class="no-padding-x"
                        @order-change="
                          (order) => onOrderChange('created', order)
                        "
                      ></i-table-sort-button>
                    </div>

                    <!-- date created -->
                    <div>
                      <i-table-sort-button
                        label="Status"
                        class="no-padding-x"
                        @order-change="
                          (order) => onOrderChange('status', order)
                        "
                      ></i-table-sort-button>
                    </div>

                    <!-- menu fill -->
                    <div></div>
                  </template>
                </i-table-row>
              </template>

              <!-- table body -->
              <template #rows>
                <i-table-row
                  v-for="webhook of displayedWebhooks"
                  :key="webhook.id"
                  class="high selectable"
                  @on-row-click="redirectToEditWebhook(webhook)"
                >
                  <template #cells>
                    <!-- url -->
                    <div class="url-link no-padding-x centered">
                      <router-link
                        :to="`/settings/webhooks/${webhook.id}/edit`"
                      >
                        <span class="i-text-subheading-2 i-text-neutral-120">
                          {{ webhook.url }}
                        </span>
                      </router-link>
                    </div>

                    <!-- event -->
                    <div class="centered">
                      <div class="i-text-body-2 i-text-neutral-120">
                        {{ webhookTypeDisplayName(webhook) }}
                      </div>
                    </div>

                    <!-- created -->
                    <div class="centered">
                      <div class="i-text-body-2 i-text-neutral-120 centered">
                        {{ formatDateTime(webhook.created_at, 'lll') }}
                      </div>
                    </div>

                    <!-- status -->
                    <div class="centered">
                      <i-badge
                        :colour="
                          webhook.enabled
                            ? iBadgeColours.SUCCESS
                            : iBadgeColours.NEUTRAL
                        "
                        :label="webhook.enabled ? 'Enabled' : 'Deactivated'"
                      ></i-badge>
                    </div>

                    <!-- menu -->
                    <div class="menu-container">
                      <i-menu
                        ref="menuEl"
                        :trigger-align="iMenuTriggerAlignOptions.RIGHT"
                        :width="250"
                      >
                        <template #trigger>
                          <i-button
                            class="trigger-button"
                            data-testid="action-menu"
                            :type="iButtonTypes.NEUTRAL"
                            :button-style="iButtonStyles.GHOST"
                            left-icon="medium-more-menu-vertical"
                          />
                        </template>

                        <template #items>
                          <!-- edit -->
                          <i-menu-item
                            label="Edit"
                            icon="small-edit-pencil"
                            @item-click="redirectToEditWebhook(webhook)"
                          ></i-menu-item>

                          <!-- delete -->
                          <i-menu-item
                            label="Delete"
                            :icon-color="palette['light-error-90']"
                            :text-color="palette['light-error-90']"
                            icon="small-delete-circle"
                            @item-click="startDeleteWebhook(webhook)"
                          ></i-menu-item>
                        </template>
                      </i-menu>
                    </div>
                  </template>
                </i-table-row>
              </template>
            </i-table>

            <div class="w-100">
              <i-pagination
                v-if="showPagination"
                size="md"
                :total-items="webhooksLength"
                :items-per-page="perPage"
                :selected-page="currentPage"
                class="i-mt-12"
                @page-click="(page) => (currentPage = page)"
              ></i-pagination>
              <i-modal
                modal-id="delete-webhook"
                data-testid="delete-webhook-modal"
                title="Delete Webhook"
                confirm-button-text="Delete"
                type="error"
                @on-submit="deleteWebhook"
              >
                <template #body-content>
                  <div>
                    Are you sure you want to delete this Webhook?<br />You
                    cannot undo this action.
                  </div>
                </template>
              </i-modal>
            </div>
          </div>
          <empty-page
            v-else-if="!loadingWebhooks"
            data-testid="webhooks-empty"
            icon="large-browser-notification"
            title="Start by creating a webhook"
            message="Get notified automatically when a selected event is completed."
            btn-text="Create Webhook"
            :btn-click="redirectToCreateWebhook"
          ></empty-page>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.menu-container {
  width: 40px;
}
</style>
